import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { UserAvatar, AvatarStack } from '@/components/UserAvatar';
import { UserMultiSelect } from '@/components/UserMultiSelect';
import { PeoplePicker } from '@/components/PeoplePicker';
import {
  getTeams, createTeam, updateTeam, deleteTeam, getTeamsPeoplePool,
  getAssignments, createAssignment, updateAssignment,
  getAllUsers, getProjects, getChecklists, getCustomChecklists, getKaizenEvents, getAudits,
} from '@/services/api';
import { toast } from 'sonner';
import {
  Plus, Users, Edit3, Trash2, ArrowLeft, Crown, UserPlus, Target,
  CheckCircle2, Clock, AlertCircle, Calendar, MessageSquare, ChevronRight,
  UserRound, Briefcase
} from 'lucide-react';

const statusColor = (s) => ({
  pending: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  blocked: 'bg-rose-100 text-rose-700',
}[s] || 'bg-slate-100 text-slate-700');

const priorityColor = (p) => ({
  low: 'bg-slate-100 text-slate-600',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-rose-100 text-rose-700',
}[p] || 'bg-slate-100 text-slate-600');

// ============ Create / Edit Team dialog ============
const TeamFormDialog = ({ open, onOpenChange, editing, pool, currentUserId, onSaved }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [responsible, setResponsible] = useState({ kind: 'user', id: currentUserId });
  const [memberUserIds, setMemberUserIds] = useState([currentUserId]);
  const [memberEmployeeIds, setMemberEmployeeIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name || '');
      setDescription(editing.description || '');
      setResponsible({
        kind: editing.responsible_kind || 'user',
        id: editing.responsible_id,
      });
      setMemberUserIds(editing.member_ids || []);
      setMemberEmployeeIds(editing.employee_ids || []);
    } else {
      setName('');
      setDescription('');
      setResponsible({ kind: 'user', id: currentUserId });
      setMemberUserIds([currentUserId]);
      setMemberEmployeeIds([]);
    }
  }, [open, editing, currentUserId]);

  // Responsible must be a member of their respective pool — keep in sync
  useEffect(() => {
    if (!responsible?.id) return;
    if (responsible.kind === 'user' && !memberUserIds.includes(responsible.id)) {
      setMemberUserIds((prev) => [...prev, responsible.id]);
    }
    if (responsible.kind === 'employee' && !memberEmployeeIds.includes(responsible.id)) {
      setMemberEmployeeIds((prev) => [...prev, responsible.id]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responsible]);

  const save = async () => {
    if (!name.trim()) { toast.error('Team name is required'); return; }
    if (!responsible?.id) { toast.error('Responsible person is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        responsible_kind: responsible.kind,
        responsible_id: responsible.id,
        member_ids: memberUserIds,
        employee_ids: memberEmployeeIds,
      };
      const result = editing
        ? await updateTeam(editing.team_id, payload)
        : await createTeam(payload);
      toast.success(editing ? 'Team updated' : 'Team created');
      onSaved(result);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message || 'Failed to save team');
    } finally {
      setSaving(false);
    }
  };

  // Responsible options are constrained to the currently selected members
  // (so people can only become responsible if they are already in the team).
  const responsiblePool = useMemo(() => ({
    users: (pool.users || []).filter(u => memberUserIds.includes(u.id)),
    employees: (pool.employees || []).filter(e => memberEmployeeIds.includes(e.id)),
  }), [pool, memberUserIds, memberEmployeeIds]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="team-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit team' : 'New team'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Team name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Warehouse A" data-testid="team-name-input" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="What does this team do?" />
          </div>
          <div>
            <Label>Members</Label>
            <PeoplePicker
              pool={pool}
              selectedUserIds={memberUserIds}
              selectedEmployeeIds={memberEmployeeIds}
              onUsersChange={(ids) => {
                if (responsible.kind === 'user' && !ids.includes(responsible.id)) {
                  toast.error('Cannot remove the responsible person. Change the responsible first.');
                  return;
                }
                setMemberUserIds(ids);
              }}
              onEmployeesChange={(ids) => {
                if (responsible.kind === 'employee' && !ids.includes(responsible.id)) {
                  toast.error('Cannot remove the responsible person. Change the responsible first.');
                  return;
                }
                setMemberEmployeeIds(ids);
              }}
              disabledKey={responsible}
              placeholder="Select members..."
              testIdPrefix="team-member"
            />
            <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2">
              <span className="inline-flex items-center gap-1"><Briefcase className="h-3 w-3 text-amber-500" />Workforce</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1"><UserRound className="h-3 w-3 text-[#2D8C8C]" />Platform user (can receive tasks &amp; chat)</span>
            </p>
          </div>
          <div>
            <Label>Responsible person *</Label>
            <PeoplePicker
              pool={responsiblePool}
              multi={false}
              singleValue={responsible}
              onSingleChange={(v) => v && setResponsible(v)}
              placeholder="Select responsible"
              testIdPrefix="team-responsible"
            />
            <p className="text-xs text-muted-foreground mt-1">Must be a team member. Cannot be removed until you pick a different responsible.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="team-save-btn">{saving ? 'Saving...' : (editing ? 'Save' : 'Create team')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ Create Task dialog ============
const TaskFormDialog = ({ open, onOpenChange, team, currentUser, onCreated }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [itemType, setItemType] = useState('custom');
  const [itemId, setItemId] = useState('');
  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [linkedOptions, setLinkedOptions] = useState([]);

  useEffect(() => {
    if (!open) return;
    setTitle(''); setDescription(''); setAssignedTo(''); setItemType('custom'); setItemId(''); setPriority('medium'); setDueDate('');
  }, [open]);

  // Fetch linked items when itemType changes
  useEffect(() => {
    if (!open || itemType === 'custom') { setLinkedOptions([]); return; }
    const fetchers = {
      project: () => getProjects().catch(() => []),
      checklist: () => getChecklists().catch(() => []),
      custom_checklist: () => getCustomChecklists().catch(() => []),
      kaizen: () => getKaizenEvents().catch(() => []),
      audit: () => getAudits().catch(() => []),
    };
    const fn = fetchers[itemType];
    if (!fn) { setLinkedOptions([]); return; }
    fn().then((data) => {
      const keyMap = {
        project: (x) => ({ id: x.project_id, label: x.title || x.name }),
        checklist: (x) => ({ id: x.checklist_id, label: x.display_name || x.title || x.template_name }),
        custom_checklist: (x) => ({ id: x.checklist_id, label: x.title }),
        kaizen: (x) => ({ id: x.event_id, label: x.title || x.name }),
        audit: (x) => ({ id: x.audit_id, label: x.title || x.template_name }),
      };
      setLinkedOptions((data || []).map(keyMap[itemType]));
    });
  }, [itemType, open]);

  const save = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    if (!assignedTo) { toast.error('Assignee is required'); return; }
    if (itemType !== 'custom' && !itemId) { toast.error('Please select a linked item'); return; }
    setSaving(true);
    try {
      await createAssignment({
        title: title.trim(),
        description: description.trim(),
        assigned_to: assignedTo,
        item_type: itemType,
        item_id: itemType === 'custom' ? `custom_${Date.now()}` : itemId,
        priority,
        due_date: dueDate || null,
        team_id: team?.team_id,
      });
      toast.success('Task delegated');
      onCreated();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message || 'Failed to create task');
    } finally {
      setSaving(false);
    }
  };

  const assigneeOptions = team?.members || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="task-form-dialog">
        <DialogHeader>
          <DialogTitle>Delegate task{team ? ` — ${team.name}` : ''}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task name" data-testid="task-title-input" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Assign to *</Label>
              <Select value={assignedTo} onValueChange={setAssignedTo}>
                <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Select member" /></SelectTrigger>
                <SelectContent>
                  {assigneeOptions.map(m => (
                    <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Task type</Label>
              <Select value={itemType} onValueChange={setItemType}>
                <SelectTrigger data-testid="task-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom (free text)</SelectItem>
                  <SelectItem value="project">DMAIC Project</SelectItem>
                  <SelectItem value="checklist">Checklist</SelectItem>
                  <SelectItem value="custom_checklist">Custom Checklist</SelectItem>
                  <SelectItem value="kaizen">Kaizen Event</SelectItem>
                  <SelectItem value="audit">6S Audit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due date</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
          </div>
          {itemType !== 'custom' && (
            <div>
              <Label>Linked {itemType.replace('_', ' ')}</Label>
              <Select value={itemId} onValueChange={setItemId}>
                <SelectTrigger data-testid="task-linked-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {linkedOptions.length === 0 && <SelectItem value="_none" disabled>No items available</SelectItem>}
                  {linkedOptions.map(o => (<SelectItem key={o.id} value={o.id}>{o.label || o.id}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="task-save-btn">{saving ? 'Saving...' : 'Delegate task'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ Teams List ============
const TeamsListView = ({ teams, onOpen, onEdit, onNew, canEdit, loading }) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Team Management</h1>
        <p className="text-sm text-muted-foreground">Create teams, delegate tasks, and track progress across the organization.</p>
      </div>
      <Button onClick={onNew} data-testid="new-team-btn"><Plus className="h-4 w-4 mr-2" />New team</Button>
    </div>

    {loading ? (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    ) : teams.length === 0 ? (
      <Card className="p-12 text-center text-muted-foreground">
        <Users className="h-12 w-12 mx-auto mb-4 opacity-30" />
        <p className="text-base">No teams yet</p>
        <p className="text-sm mt-1">Create your first team to start delegating tasks.</p>
      </Card>
    ) : (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="teams-grid">
        {teams.map(team => (
          <Card
            key={team.team_id}
            className="p-5 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => onOpen(team.team_id)}
            data-testid={`team-card-${team.team_id}`}
          >
            <div className="flex items-start justify-between gap-2 mb-3">
              <h3 className="font-semibold text-base truncate">{team.name}</h3>
              {canEdit(team) && (
                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={(e) => { e.stopPropagation(); onEdit(team); }} data-testid={`team-edit-${team.team_id}`}>
                  <Edit3 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            {team.description && <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{team.description}</p>}
            <div className="flex items-center gap-2 text-xs mb-3">
              <Crown className="h-3.5 w-3.5 text-amber-500 shrink-0" />
              <UserAvatar user={{ user_id: team.responsible_id, name: team.responsible_name }} size="xs" />
              <span className="truncate">{team.responsible_name}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <AvatarStack users={team.members || []} size="xs" max={5} />
              <span className="text-muted-foreground">
                {((team.members || []).length + (team.employee_members || []).length)} member{((team.members || []).length + (team.employee_members || []).length) !== 1 ? 's' : ''}
                {(team.employee_members || []).length > 0 && (
                  <span className="ml-1 text-amber-600 dark:text-amber-400">· {team.employee_members.length} workforce</span>
                )}
              </span>
            </div>
          </Card>
        ))}
      </div>
    )}
  </div>
);

// ============ Team Detail ============
const TeamDetailView = ({ team, tasks, loading, onBack, onEdit, onNewTask, onDelete, canEdit, onMarkStatus }) => {
  const [tab, setTab] = useState('overview');
  const completed = tasks.filter(t => t.status === 'completed').length;
  const total = tasks.length;
  const pct = total ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold truncate" data-testid="team-detail-title">{team.name}</h1>
          {team.description && <p className="text-sm text-muted-foreground">{team.description}</p>}
        </div>
        {canEdit && (
          <>
            <Button variant="outline" onClick={onEdit} data-testid="team-detail-edit">
              <Edit3 className="h-4 w-4 mr-2" />Edit team
            </Button>
            <Button variant="outline" onClick={onDelete} className="text-rose-600 hover:bg-rose-50" data-testid="team-detail-delete">
              <Trash2 className="h-4 w-4" />
            </Button>
          </>
        )}
        <Button onClick={onNewTask} data-testid="team-detail-new-task">
          <Plus className="h-4 w-4 mr-2" />Delegate task
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Members</p>
          <p className="text-2xl font-semibold mt-1" data-testid="stat-members">{((team.members || []).length + (team.employee_members || []).length)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Total tasks</p>
          <p className="text-2xl font-semibold mt-1" data-testid="stat-total">{total}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Completed</p>
          <p className="text-2xl font-semibold mt-1 text-emerald-600" data-testid="stat-completed">{completed}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Completion</p>
          <p className="text-2xl font-semibold mt-1" data-testid="stat-pct">{pct}%</p>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">Tasks</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : tasks.length === 0 ? (
            <Card className="p-10 text-center text-muted-foreground">
              <Target className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No tasks delegated yet</p>
            </Card>
          ) : (
            <div className="space-y-2" data-testid="team-tasks">
              {tasks.map(t => (
                <Card key={t.assignment_id} className="p-4" data-testid={`task-${t.assignment_id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h4 className="font-semibold truncate">{t.title}</h4>
                        <Badge className={statusColor(t.status)}>{t.status.replace('_', ' ')}</Badge>
                        <Badge className={priorityColor(t.priority)}>{t.priority}</Badge>
                        <Badge variant="outline" className="text-[10px]">{t.item_type}</Badge>
                      </div>
                      {t.description && <p className="text-xs text-muted-foreground mb-2">{t.description}</p>}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1.5">
                          <UserAvatar user={{ user_id: t.assigned_to, name: t.assigned_to_name }} size="xs" />
                          {t.assigned_to_name}
                        </span>
                        <span>by {t.assigned_by_name}</span>
                        {t.due_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{t.due_date.slice(0, 10)}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {t.status !== 'completed' && (
                        <Button variant="outline" size="sm" onClick={() => onMarkStatus(t, 'completed')} data-testid={`task-complete-${t.assignment_id}`}>
                          <CheckCircle2 className="h-4 w-4 mr-1" />Complete
                        </Button>
                      )}
                      {t.status === 'pending' && (
                        <Button variant="outline" size="sm" onClick={() => onMarkStatus(t, 'in_progress')}>
                          <Clock className="h-4 w-4 mr-1" />Start
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="members" className="mt-4">
          <Card className="p-1">
            {(team.employee_members || []).map(m => {
              const isResp = team.responsible_kind === 'employee' && m.employee_id === team.responsible_id;
              return (
                <div key={`emp-${m.employee_id}`} className="flex items-center gap-3 p-3 border-b last:border-b-0" data-testid={`team-member-row-emp-${m.employee_id}`}>
                  <div className="w-8 h-8 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Briefcase className="h-4 w-4 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{m.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Workforce{m.uzk ? ` · ${m.uzk}` : ''}{m.pers_nr ? ` · #${m.pers_nr}` : ''}
                    </p>
                  </div>
                  {isResp && (
                    <Badge variant="secondary" className="gap-1"><Crown className="h-3 w-3 text-amber-500" />Responsible</Badge>
                  )}
                </div>
              );
            })}
            {(team.members || []).map(m => {
              const isResp = (team.responsible_kind || 'user') === 'user' && m.user_id === team.responsible_id;
              return (
                <div key={m.user_id} className="flex items-center gap-3 p-3 border-b last:border-b-0" data-testid={`team-member-row-${m.user_id}`}>
                  <UserAvatar user={m} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium flex items-center gap-1.5">{m.name} <UserRound className="h-3 w-3 text-[#2D8C8C]/70" /></p>
                    <p className="text-xs text-muted-foreground">{m.email}</p>
                  </div>
                  {isResp && (
                    <Badge variant="secondary" className="gap-1"><Crown className="h-3 w-3 text-amber-500" />Responsible</Badge>
                  )}
                  {m.user_id === team.created_by && !isResp && (
                    <Badge variant="outline">Creator</Badge>
                  )}
                </div>
              );
            })}
            {(team.members || []).length === 0 && (team.employee_members || []).length === 0 && (
              <p className="p-6 text-center text-sm text-muted-foreground">No members yet</p>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ============ Main page ============
export const TeamManagementPage = () => {
  const { user, isAdmin } = useAuth();
  const params = useParams();
  const navigate = useNavigate();

  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [users, setUsers] = useState([]);
  const [pool, setPool] = useState({ users: [], employees: [] });
  const [showForm, setShowForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailTasks, setDetailTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);

  const canEditTeam = useCallback((t) =>
    !!t && (user?.is_master_admin || user?.role === 'master_admin' || t.created_by === user?.user_id),
    [user]);

  const loadTeams = useCallback(async () => {
    try {
      const data = await getTeams();
      setTeams(data || []);
    } catch {
      toast.error('Failed to load teams');
    } finally {
      setLoadingTeams(false);
    }
  }, []);

  useEffect(() => { loadTeams(); }, [loadTeams]);

  useEffect(() => {
    getAllUsers().then(setUsers).catch(() => {});
    getTeamsPeoplePool().then(setPool).catch(() => {});
  }, []);

  // Open detail from route param
  useEffect(() => {
    if (!params.id) { setDetail(null); setDetailTasks([]); return; }
    const t = teams.find(t => t.team_id === params.id);
    if (t) setDetail(t);
    setLoadingTasks(true);
    getAssignments(null, null, params.id)
      .then(setDetailTasks)
      .catch(() => toast.error('Failed to load tasks'))
      .finally(() => setLoadingTasks(false));
  }, [params.id, teams]);

  const handleTeamSaved = (team) => {
    setTeams(prev => {
      const existing = prev.find(p => p.team_id === team.team_id);
      if (existing) return prev.map(p => p.team_id === team.team_id ? team : p);
      return [team, ...prev];
    });
    if (detail && detail.team_id === team.team_id) setDetail(team);
    setEditingTeam(null);
  };

  const handleDeleteTeam = async () => {
    if (!detail) return;
    if (!window.confirm(`Delete team "${detail.name}"? Existing tasks will be preserved but unlinked from the team.`)) return;
    try {
      await deleteTeam(detail.team_id);
      toast.success('Team deleted');
      navigate('/teams');
      loadTeams();
    } catch (e) {
      toast.error(e.message || 'Failed to delete');
    }
  };

  const markStatus = async (task, status) => {
    try {
      await updateAssignment(task.assignment_id, { status, progress: status === 'completed' ? 100 : 50 });
      setDetailTasks(prev => prev.map(t => t.assignment_id === task.assignment_id ? { ...t, status, progress: status === 'completed' ? 100 : 50 } : t));
    } catch (e) {
      toast.error(e.message || 'Failed to update task');
    }
  };

  const reloadTasks = () => {
    if (!detail) return;
    setLoadingTasks(true);
    getAssignments(null, null, detail.team_id).then(setDetailTasks).finally(() => setLoadingTasks(false));
  };

  const sortedTeams = useMemo(() =>
    [...teams].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')),
    [teams]);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-4" data-testid="team-management-page">
        {detail ? (
          <TeamDetailView
            team={detail}
            tasks={detailTasks}
            loading={loadingTasks}
            onBack={() => { setDetail(null); navigate('/teams'); }}
            onEdit={() => { setEditingTeam(detail); setShowForm(true); }}
            onNewTask={() => setShowTaskForm(true)}
            onDelete={handleDeleteTeam}
            canEdit={canEditTeam(detail)}
            onMarkStatus={markStatus}
          />
        ) : (
          <TeamsListView
            teams={sortedTeams}
            loading={loadingTeams}
            canEdit={canEditTeam}
            onOpen={(id) => navigate(`/teams/${id}`)}
            onEdit={(t) => { setEditingTeam(t); setShowForm(true); }}
            onNew={() => { setEditingTeam(null); setShowForm(true); }}
          />
        )}
      </div>

      <TeamFormDialog
        open={showForm}
        onOpenChange={setShowForm}
        editing={editingTeam}
        pool={pool}
        currentUserId={user?.user_id}
        onSaved={handleTeamSaved}
      />

      {detail && (
        <TaskFormDialog
          open={showTaskForm}
          onOpenChange={setShowTaskForm}
          team={detail}
          currentUser={user}
          onCreated={reloadTasks}
        />
      )}
    </DashboardLayout>
  );
};

export default TeamManagementPage;
